import React, { useEffect, useState } from "react";

import { CButton, CCard, CCardHeader, CCardSubtitle } from "@coreui/react";
import { toastr } from "react-redux-toastr";
import Select from "react-select";
import { InputGroup, Label } from "reactstrap";

import Breadcrumb from "../../../../../components/Breadcrumb";
import LoadingStatistics from "../../../../../components/LoadingStatistics";
import ProtectedRoute from "../../../../../components/ProtectedRoute";
import StatisticFiltersCompare from "../../../../../components/StatisticFiltersCompare";
import AcertosPorHabilidadePainel from "../../../../../components/Statistics/AcertosPorHabilidade/Painel";
import URL from "../../../../../config/api";
import axios from "../../../../../config/axios";
import "../style.css";

// Quantas provas do ADR o select carrega. A rota é paginada e não há busca
// incremental aqui: uma página grande cobre o histórico de um grupo.
const EXAM_PAGE_SIZE = 200;

// Relatório da avaliação diagnóstica (?type=diagnostic em /evaluation/adr/report).
//
// Ao contrário do relatório padrão, os dados NÃO vêm do backend do AVA: a prova
// é uma prova do ADR e os acertos por habilidade vêm da rota
// `statistics/ava-report-abilities/:examId` do backend do ADR, que já devolve o
// formato `{ abilities, applications }` esperado pelo painel.
//
// O corpo do relatório é o mesmo conteúdo da aba "Habilidade" das Estatísticas
// (components/Statistics/AcertosPorHabilidade/Painel).
// Recebe user/general por props do componente conectado em ../index.jsx.
const DiagnosticEvaluationReport = props => {
  const [loading, setLoading] = useState(false);
  const [loadingExams, setLoadingExams] = useState(true);
  const [filtered, setFiltered] = useState(false);
  const [selected, setSelected] = useState([]);

  const [dataCorrectsByAbility, setDataCorrectsByAbility] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [examList, setExamList] = useState([]);
  const [examId, setExamId] = useState("");

  // Provas do ADR do grupo ativo. roleId/userId vão para o ADR restringir a
  // lista quando quem acessa é professor (ele só vê as provas das séries que
  // leciona) — as demais roles recebem tudo do grupo.
  useEffect(() => {
    const groupId = props.general.active_roles.group_id;

    if (!groupId) {
      setLoadingExams(false);
      return;
    }

    axios
      .get(`${URL.adrApi}exam/list-exams-by-group-id/${groupId}`, {
        params: {
          page: 0,
          pageSize: EXAM_PAGE_SIZE,
          sortBy: "name",
          sortOrder: "asc",
          roleId: props.user.user_roles?.[0]?.role_id,
          userId: props.user.user?.id
        }
      })
      .then(resp => {
        setExamList(resp.data.data?.exams ?? []);
      })
      .catch(() => {
        toastr.error("Erro", "Não foi possível carregar as avaliações do ADR.");
      })
      .finally(() => {
        setLoadingExams(false);
      });
  }, []);

  useEffect(() => {
    axios
      .get(`${URL.api}access/role/getMenuPhoto`, {
        params: {
          school_id: props.general.active_roles.school_id,
          group_id: props.general.active_roles.group_id
        },
        headers: { authorization: `Bearer ${props.user.token}` }
      })
      .then(resp => {
        setPhoto(resp.data.data);
      });
  }, []);

  const changeFilter = selected => {
    setLoading(true);
    setSelected(selected);

    // O filtro usa 0 para "todos"; o ADR entende a ausência do parâmetro, então
    // os zeros são descartados em vez de virarem um filtro por id 0.
    const params = {
      groupId: selected.groupId || undefined,
      regionId: selected.regionId || undefined,
      schoolId: selected.schoolId || undefined,
      roomId: selected.roomId || undefined
    };

    axios
      .get(`${URL.adrApi}statistics/ava-report-abilities/${examId}`, { params })
      .then(resp => {
        setDataCorrectsByAbility(resp.data.data);
        setFiltered(true);
      })
      .catch(() => {
        setDataCorrectsByAbility([]);
        setFiltered(true);
        toastr.error(
          "Erro",
          "Não foi possível carregar os acertos por habilidade."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changeExam = exam => {
    setExamId(exam.id);
    setFiltered(false);
    setDataCorrectsByAbility([]);
  };

  const selectedExam = examList.find(item => item.id === examId);

  return (
    <>
      <Breadcrumb current={props.currentRoute} />
      <div className="d-flex no-print flex-column">
        <div>
          <h2>Relatório de Avaliação Diagnóstica</h2>
          <p className="text-muted">
            Crie e visualize relatórios de avaliações diagnósticas.
          </p>
        </div>
        {/* O select cresce para ocupar a linha e o Imprimir fica ao lado dele.
            Em telas estreitas (< sm) o botão desce, por isso o mb-2 mb-sm-0. */}
        <div className="d-flex flex-wrap align-items-end mt-2 w-100">
          <div
            className="flex-grow-1 mr-2 mb-2 mb-sm-0"
            style={{ minWidth: "240px" }}
          >
            <Label className="d-block w-100 text-ellipsis">Avaliação</Label>
            <InputGroup className="w-100">
              <Select
                className="w-100"
                value={selectedExam ?? null}
                placeholder="Selecione uma avaliação"
                onChange={changeExam}
                options={examList}
                getOptionLabel={option => option.name}
                getOptionValue={option => option.id}
                isLoading={loadingExams}
                loadingMessage={() => "Carregando..."}
                noOptionsMessage={() => "Nenhuma avaliação encontrada"}
              />
            </InputGroup>
          </div>
          <div>
            <CButton
              color="primary"
              onClick={() => window.print()}
              disabled={loading}
            >
              <i className="fa fa-print mr-2"></i>
              Imprimir
            </CButton>
          </div>
        </div>
        <div className="row my-3 w-100">
          <div className="col-12">
            {/* Sem activityId: a prova é do ADR, não existe atividade do AVA
                para recortar as alocações. O componente então carrega a
                hierarquia que o usuário pode ver, e o recorte de
                grupo/regional/escola/turma vai para o ADR.

                emptySelection: os campos abrem em branco, exceto os que o papel
                do usuário fixa. autoSubmit={examId}: trocar de avaliação já
                refaz a busca, mantendo o recorte escolhido. */}
            {examId && (
              <StatisticFiltersCompare
                onChange={changeFilter}
                hideUser
                emptySelection
                autoSubmit={examId}
              />
            )}
          </div>
        </div>
      </div>
      {loading ? (
        <LoadingStatistics />
      ) : (
        filtered && (
          <>
            <HeaderReport
              title={selectedExam?.name}
              selected={selected}
              photo={photo}
            />
            {/* allocateQuery: o "Disponibilizar" de uma atividade sugerida abre
                /activity/allocate/:id numa aba nova com estes queryParams, e os
                filtros de lá já abrem no mesmo recorte.

                Os campos vazios (0) não viram parâmetro — é o "Todas". Sem
                room_id, a alocação marca todas as turmas do recorte. O
                type=diagnostic diz de onde o link veio: é o que libera, lá,
                Regional e Escola em "Todas". */}
            {/* chartRowHeight: a altura do gráfico passa a ser função do número
                de habilidades, em vez da razão 2:1 fixa do Chart.js. Com poucas
                habilidades ele não vira um bloco alto e vazio, com muitas as
                barras não achatam — e na impressão ele pode ocupar a largura
                toda sem estourar a página. */}
            <AcertosPorHabilidadePainel
              data={dataCorrectsByAbility}
              chartRowHeight={44}
              exactPercentage
              allocateQuery={{
                type: "diagnostic",
                group_id: selected.groupId,
                region_id: selected.regionId,
                school_id: selected.schoolId,
                room_id: selected.roomId
              }}
            />
          </>
        )
      )}
      <ProtectedRoute routeId={props.currentRoute} />
    </>
  );
};

function HeaderReport({ title, selected, photo }) {
  return (
    <>
      <CCard className="d-none print-only">
        <CCardHeader className="d-flex align-items-center justify-content-between p-4">
          <div className="d-inline-flex align-items-center">
            <div className="flex-1 my-auto mr-2">
              <img src={URL.img + photo} />
            </div>
            {selected.hierarchy?.length > 0 && (
              <div className="d-flex w-100 flex-column">
                <CCardSubtitle>
                  {
                    selected.hierarchy[0].regions
                      .find(region => region.id === selected.regionId)
                      ?.schools.find(school => school.id === selected.schoolId)
                      ?.name
                  }
                </CCardSubtitle>
                <CCardSubtitle className="small my-2">
                  {
                    selected.hierarchy[0].regions.find(
                      item => item.id === selected.regionId
                    )?.name
                  }
                </CCardSubtitle>
                <CCardSubtitle className="small">
                  {
                    selected.hierarchy[0].regions
                      .find(region => region.id === selected.regionId)
                      ?.schools.find(school => school.id === selected.schoolId)
                      ?.rooms.find(room => room.id === selected.roomId)?.name
                  }
                </CCardSubtitle>
              </div>
            )}
          </div>
          <div className="d-flex-column justify-content-between">
            <CCardSubtitle className="small mb-2">{title}</CCardSubtitle>
            <CCardSubtitle className="small">
              Última atualização: {new Date().toLocaleString()}
            </CCardSubtitle>
          </div>
        </CCardHeader>
      </CCard>
    </>
  );
}

export default DiagnosticEvaluationReport;
